import type { SearchResultKeys } from '../types/search';
import type { AuthorType } from '../types/shared';

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[];
  _paq: Record<string, any>[];
};

declare const window: WindowWithDataLayer;

export const onThemeChange = (mode: 'dark' | 'light' | 'auto') => {
  window.dataLayer?.push({ event: 'header_theme_selector', theme_value: mode });

  window._paq?.push(['trackEvent', 'header_theme_selector', mode]);
};

export const onActivitiesClick = () => {
  window.dataLayer?.push({ event: 'header_user_activities' });

  window._paq.push(['trackEvent', 'header_user_activities']);
};

export const onMyZoomitClick = () => {
  window.dataLayer?.push({ event: 'header_user_myzoomit' });

  window._paq.push(['trackEvent', 'header_user_myzoomit']);
};

export const onSettingsClick = () => {
  window.dataLayer?.push({ event: 'header_user_setting' });

  window._paq.push(['trackEvent', 'header_user_setting']);
};

// article -------------------------------------------------------------------------------------------------------------
export const onRelatedArticleClick = (template: string) => {
  window.dataLayer?.push({
    event: 'article_related_article',
    template: template,
  });

  window._paq.push(['trackEvent', 'article_related_article', template]);
};

export const onShareArticle = (method: string) => {
  window.dataLayer?.push({
    event: `article_share`,
    method,
  });

  window._paq.push(['trackEvent', 'article_share', method]);
};

export const onBookmarkArticle = (authors: AuthorType[]) => {
  const authorsName = authors.map(({ fullName }) => fullName).join(' , ');

  window.dataLayer?.push({ event: 'article_bookmark', authors: authorsName });

  window._paq.push(['trackEvent', 'article_bookmark', authorsName]);
};

export const onLikeArticle = (authors: AuthorType[]) => {
  const authorsName = authors.map(({ fullName }) => fullName).join(' , ');
  window.dataLayer?.push({
    event: 'article_like',
    authors: authorsName,
  });

  window._paq.push(['trackEvent', 'article_like', authorsName]);
};

// search -------------------------------------------------------------------------------------------------------------
type SearchResultEventType = {
  title: string;
  section: SearchResultKeys | 'products';
};

export const onSearchTerm = (query: string) => {
  window.dataLayer?.push({
    event: `search`,
    search_term: query,
  });

  window._paq.push(['trackEvent', 'search', query]);
};

export const onSearchResultClick = ({ title, section }: SearchResultEventType) => {
  const sectionVal = section === 'articles' ? 'stories' : section === 'groupings' ? 'categories' : section;

  window.dataLayer?.push({
    event: `search_result`,
    search_section: sectionVal,
    clicked_title: `text of clicked item: ${title}`,
  });

  window._paq.push(['trackEvent', 'search_result', sectionVal, title]);
};

// comment ---------------------------------------------------------------------------------

export const onComment = (articleId: string) => {
  window.dataLayer?.push({ event: 'article_comment_submit', article_id: articleId });

  window._paq.push(['trackEvent', 'article_comment_submit', articleId]);
};

export const onLikeComment = (articleId: string) => {
  window.dataLayer?.push({ event: 'article_comment_like', article_id: articleId });

  window._paq.push(['trackEvent', 'article_comment_like', articleId]);
};

export const onReportComment = (articleId: string, feedbackId: string, reason: string) => {
  window.dataLayer?.push({
    event: 'article_comment_report',
    article_id: articleId,
    comment_id: feedbackId,
    report_reason: reason,
  });

  window._paq.push(['trackEvent', 'article_comment_report', reason, articleId]);
};

export const onShowMoreComment = () => {
  window.dataLayer?.push({ event: 'article_comment_showmore' });

  window._paq.push(['trackEvent', 'article_comment_showmore']);
};

export const onUserClick = (userId: string) => {
  window.dataLayer?.push({ event: 'article_comment_profile', target_user_id: userId });

  window._paq.push(['trackEvent', 'article_comment_report’, ‘url', userId]);
};

export const onSort = (sort: string) => {
  window.dataLayer?.push({ event: 'article_comment_sorting', comment_sorting: sort });

  window._paq.push(['trackEvent', 'article_comment_sorting', sort]);
};

export const onFilter = (filter: string) => {
  window.dataLayer?.push({ event: 'article_comment_tab', comment_tab: filter });

  window._paq.push(['trackEvent', 'article_comment_tab', filter]);
};

// header ---------------------------------------------------------------------------------

export const onMenuClick = ({ menu, device }: { menu: string; device: 'desktop' | 'mobile' }) => {
  window.dataLayer?.push({ event: 'header_menu', menu_item: menu, device });

  window._paq.push(['trackEvent', 'header_menu', menu, device]);
};

export const onNotificationClick = () => {
  window.dataLayer?.push({ event: 'header_notification_bell' });

  window._paq.push(['trackEvent', 'header_notification_bell']);
};

// advertising -------------------------------------------------------------------------------------------------------------

type AdTag = {
  zone: string;
  bannerId: string;
  bannerName: string;
};

export const onAdClickTag = ({ bannerId, bannerName, zone }: AdTag) => {
  window.dataLayer?.push({
    event: `ad_click`,
    ad_zone: zone,
    ad_name: bannerName,
    banner_id: bannerId,
  });

  window._paq.push(['trackEvent', 'ad_click', `${zone}-${bannerName}`, bannerId]);
};

export const onAdCloseTag = (zone: string) => {
  window.dataLayer?.push({
    event: 'banner_close',
    ad_zone: zone,
  });

  window._paq.push(['trackEvent', 'banner_close', zone]);
};

// modules ---------------------------------------------------------------------------------

export const handleModuleEvent = ({
  moduleType,
  moduleName,
  moduleTemplate,
  clickedPosition,
}: {
  moduleType: string;
  moduleName: string;
  moduleTemplate: string;
  clickedPosition: string;
}) => {
  window.dataLayer?.push({
    event: moduleType,
    module_name: moduleName,
    module_template: moduleTemplate,
    clicked_on: clickedPosition,
  });

  window._paq.push(['trackEvent', moduleType, moduleTemplate]);
};

// chips ---------------------------------------------------------------------------------

export const handleChipEvent = ({
  moduleType,
  moduleName,
  moduleTemplate,
  chipTitle,
}: {
  moduleType: string;
  moduleName: string;
  moduleTemplate: string;
  chipTitle: string;
}) => {
  window.dataLayer?.push({
    event: moduleType,
    module_name: moduleName,
    module_template: moduleTemplate,
    chip: chipTitle,
  });

  window._paq.push(['trackEvent', moduleType, moduleTemplate, chipTitle]);
};
